import React, { useEffect, useRef, useState } from "react";
import { subscribeHotspot, getHotspot, HOTSPOT_ACCENT,
  subscribeActiveHotspot, getActiveHotspot, setActiveHotspot,
  setHotspotsShown, subscribeHotspotsShown, getHotspotsShown } from "./fitHotspotStore";
import mi10Manifests from "./fitFeatures.MI10.json";
import m1841Manifests from "./fitFeatures.1841.json";
import jw11Manifests from "./fitFeatures.JW11.json";
import f1820Manifests from "./fitFeatures.1820.json";
import mi16Manifests from "./fitFeatures.MI16.json";

// Static feature manifests, keyed by `${product}_${shape}_${size}`. MI10 covers
// all 18 bodies (Round/Teardrop/Bell × B/C/D × 34/40); each body's anchors are
// placed from that body's own bra mask, so features point at the real feature
// per cup/shape/size rather than borrowing one reference body.
const MANIFESTS = { ...mi10Manifests, ...m1841Manifests, ...jw11Manifests, ...f1820Manifests, ...mi16Manifests };

const DWELL = 3500;
const VW = 1920, VH = 1080;

// Push-in geometry. The turntable frame + this overlay share the same transform
// (see RenderTurntable): base `scale(1.08)` about origin center 22%. When a fit
// answer is active we swap that for a zoomed transform biased toward the
// feature — a cinematic push-in. Because the frame <img> and this SVG apply the
// IDENTICAL transform, the pin stays glued to the feature through the zoom.
const BASE_S = 1.08;
const PUSH_S = 1.34;
const ORIGIN_X = 0.5, ORIGIN_Y = 0.22; // matches transformOrigin "center 22%"
const FRAME_Y = 0.46;                  // where we try to seat the feature vertically
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Build the transform string shared by the frame img and this overlay.
// Push-in DISABLED: hotspots are shown on the base frame (no cinematic zoom),
// so the shopper keeps the full body in view while a feature is called out.
function transformFor(ax, ay, scaleX, zoom) { // eslint-disable-line no-unused-vars
  return `translate(0%, 0%) scale(${BASE_S}) scaleX(${scaleX})`;
}

// Overlay that sits on the turntable stage and calls out a fit feature with a
// cinematic push-in (the frame zooms toward the feature) plus a frosted card
// naming it. It reports the desired transform up via `onZoom` so RenderTurntable
// can apply the same zoom to the frame <img>; it draws the pin/glow itself.
// Scoped to the Your Fit card (fitActive) and to bodies with a manifest.
export default function FitHotspots({ product, shape, size, angle, setAngle, scaleX = 1, onZoom }) {
  const body = `${product}_${shape}_${size}`;
  // Exact body match, else this product's reference manifest (normalised anchors
  // are close enough across the same garment's shapes/sizes).
  const manifest = MANIFESTS[body] || MANIFESTS[`${product}_Round_D34`] || null;
  const [hsOn, setHsOn] = useState(getHotspotsShown()); // master on/off (shared with turntable)
  const [seq, setSeq] = useState(getHotspot());          // the hotspot set for this screen
  const [stepIdx, setStepIdx] = useState(0);             // which ONE is showing now
  const timer = useRef(null);
  const svgRef = useRef(null);

  // Master on/off — driven by the turntable's intro/drag lifecycle AND the toggle.
  useEffect(() => subscribeHotspotsShown(setHsOn), []);
  // The hotspot set for the current screen.
  useEffect(() => {
    const cur = getHotspot();
    if (cur) { setSeq(cur); setStepIdx(0); }
    return subscribeHotspot((s) => { setSeq(s); setStepIdx(0); });
  }, []);
  // A chat answer / pin tap jumps the ONE shown hotspot to that feature.
  useEffect(() => {
    const jump = (step) => {
      if (!step) return;
      setSeq((cur) => {
        const list = cur || [];
        const i = list.findIndex((s) => s.feature === step.feature && s.sub === step.sub);
        if (i >= 0) setStepIdx(i);
        return list;
      });
    };
    jump(getActiveHotspot());
    return subscribeActiveHotspot(jump);
  }, []);

  // Show one hotspot at a time; cycle through the set, jumping the frame to each
  // step's angle so the feature is on-screen.
  useEffect(() => {
    if (!hsOn || !seq || !seq.length) return undefined;
    const step = seq[stepIdx];
    if (step && setAngle && typeof step.angle === "number") setAngle(step.angle);
    if (seq.length > 1) {
      timer.current = setTimeout(() => setStepIdx((i) => (i + 1) % seq.length), DWELL);
      return () => clearTimeout(timer.current);
    }
    return undefined;
  }, [hsOn, seq, stepIdx, setAngle]);

  // No push-in: keep the frame at its base transform (full body in view). The
  // overlay shares that base transform, so the pin sits on its feature.
  useEffect(() => { if (onZoom) onZoom(null); }, [onZoom]);
  useEffect(() => () => { if (onZoom) onZoom(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accent = HOTSPOT_ACCENT;
  const xform = transformFor(0.5, 0.5, scaleX, false);
  const toggleHotspots = (v) => setHotspotsShown(v);

  // Nothing to show without a manifest AND a hotspot set for this screen.
  if (!manifest || !seq || !seq.length) return null;
  // Hotspots off → just the toggle, so the shopper can switch them back on.
  if (!hsOn) {
    return <Toggle on={false} setOn={toggleHotspots} />;
  }

  // Resolve the ONE current hotspot at the current frame.
  const step = seq[stepIdx] || seq[0];
  const key = String(angle).padStart(3, "0");
  const rec = step ? (manifest.frames[key] || {})[step.feature] : null;
  const pt = rec && (rec[step.sub] || rec.center);
  const vis = rec ? Math.min(1, (rec.px || 0) / ((manifest.peak[step.feature] || 1) * 0.55)) : 0;
  const show = !!(pt && vis >= 0.12);
  const gx = (show ? pt[0] : 0.5) * VW, gy = (show ? pt[1] : 0.5) * VH;

  return (
    <>
      {/* One pin at a time. Shares the frame's base transform so it stays glued
          to its feature. No push-in, no dotted leader. */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VW} ${VH}`}
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          transform: xform, transformOrigin: "center 22%",
          pointerEvents: "none", zIndex: 6, opacity: show ? 1 : 0, transition: "opacity .3s",
        }}
      >
        {/* pulse */}
        <circle cx={gx} cy={gy} r="18" fill="none" stroke={accent} strokeWidth="3" opacity="0.55">
          <animate attributeName="r" values="16;46" dur="1.9s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.55;0" dur="1.9s" repeatCount="indefinite" />
        </circle>
        {/* pin */}
        <circle cx={gx} cy={gy} r="12" fill={accent} stroke="#fff" strokeWidth="3" />
      </svg>

      {/* Description box for the shown feature — with a next-arrow that advances
          to the following feature. */}
      <FrostedCard
        show={show}
        accent={accent}
        tag={step.tag}
        note={step.note}
        hasNext={seq.length > 1}
        onNext={() => setStepIdx((i) => (i + 1) % seq.length)}
      />

      <Toggle on={hsOn} setOn={toggleHotspots} />
    </>
  );
}

function FrostedCard({ show, accent, tag, note, onNext, hasNext }) {
  return (
    <div
      className="RenderTurntable__Callout"
      style={{
        // Full-width at the stage bottom — the size bubble + spin CTA clear out
        // while a callout is up, so the card owns this row.
        position: "absolute", left: 12, right: 12, bottom: 12, zIndex: 7,
        padding: "11px 14px", borderRadius: 14,
        background: "rgba(18,20,26,0.5)",
        backdropFilter: "blur(16px) saturate(1.2)",
        WebkitBackdropFilter: "blur(16px) saturate(1.2)",
        border: "1px solid rgba(255,255,255,0.16)",
        boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
        // The card passes drags through; only the next-arrow is interactive.
        pointerEvents: "none",
        display: "flex", flexDirection: "row", alignItems: "center", gap: 10,
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(120%)",
        transition: "transform .34s cubic-bezier(.3,.7,.2,1), opacity .3s",
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: accent, flex: "none",
            boxShadow: "0 0 0 2px #fff" }} />
          <span style={{ color: "#fff", fontSize: 14, fontWeight: 650, letterSpacing: "-0.01em",
            fontFamily: "-apple-system,system-ui,sans-serif" }}>{tag}</span>
        </div>
        {note && (
          <span style={{ color: "rgba(255,255,255,0.78)", fontSize: 12, lineHeight: 1.45,
            paddingLeft: 18, fontFamily: "-apple-system,system-ui,sans-serif" }}>{note}</span>
        )}
      </div>
      {hasNext && (
        <button
          type="button"
          aria-label="Next feature"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onNext}
          style={{
            flex: "none", width: 32, height: 32, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff", cursor: "pointer", padding: 0, pointerEvents: "auto",
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}
    </div>
  );
}

function Toggle({ on, setOn }) {
  return (
    <div
      onPointerDown={(e) => e.stopPropagation()}
      onClick={() => setOn(!on)}
      style={{
        position: "absolute", top: 12, right: 12, zIndex: 8, display: "flex",
        alignItems: "center", gap: 8, padding: "6px 11px", borderRadius: 22, cursor: "pointer",
        fontSize: 12, fontWeight: 600, color: "#fff",
        background: "rgba(20,22,28,0.6)", backdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.18)",
      }}
    >
      <span>Features</span>
      <span style={{
        width: 28, height: 16, borderRadius: 20, position: "relative",
        background: on ? HOTSPOT_ACCENT : "#3a4152", transition: "background .2s",
      }}>
        <span style={{
          position: "absolute", top: 2, left: on ? 14 : 2, width: 12, height: 12, borderRadius: "50%",
          background: "#fff", transition: "left .2s",
        }} />
      </span>
    </div>
  );
}
